/**
 * `MapView` — Google Maps JS API wrapper with sport-themed clustered pins.
 *
 * Composition:
 *   - Loads the Maps JS API on mount via the `@googlemaps/js-api-loader` v2
 *     functional API (`setOptions` + `importLibrary`), matching `MapPicker`.
 *   - Subscribes to the map's `idle` event to keep `bounds` + `zoom` in sync.
 *   - Feeds `(venues, bounds, zoom)` to `useMapClusters` which runs the
 *     supercluster index and returns visible cluster + point features.
 *   - For each feature emits one `Marker` with a sport-themed pin (single
 *     venue) or count bubble (cluster). Cluster click zooms in; venue click
 *     invokes `onVenueClick`.
 *
 * Notes:
 *   - Lebanon center default (33.9 N, 35.5 E) used when no `initialBounds`.
 *   - `gestureHandling: 'greedy'` so the mouse wheel zooms the map directly
 *     (no Ctrl/Cmd modifier needed); one-finger drag pans.
 *   - Marker icons embed cached Lift artwork into self-contained SVG data URLs.
 *     Only the pin shell follows `useTokens`; equipment keeps its natural colors.
 *   - `VenueListItem.lat` / `lng` arrive as `string` from the API (numeric
 *     DB column); we parse them in `useMapClusters`.
 *   - This component is browser-only; in jsdom tests we mock the loader.
 *   - The `VenueListItem` and `VenueTier` types are declared locally to keep
 *     `@bravemove/ui-web` free of a runtime workspace dep on `@bravemove/types`; the
 *     local shape is structurally compatible with the real `VenueListItem`
 *     in `packages/types/src/venue.ts`.
 *   - The Google Maps namespace types (`Map`, `Marker`) are derived from the
 *     library results of `importLibrary` instead of the global `google.maps.*`
 *     namespace — that namespace is exposed by `@types/google.maps` but is
 *     not transitively visible to `@bravemove/ui-web` (the package's `types`
 *     compiler option is restricted to `["node"]`).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { usePalette, useTokens } from '@bravemove/design-tokens/react';
import type { SportName } from '@bravemove/design-tokens/icons';
import { useMapClusters, type MapBounds, type VenueListItem } from './useMapClusters';
import { composeVenuePinSvg } from './composeVenuePinSvg';
import { composeClusterBubbleSvg } from './composeClusterBubbleSvg';
import { buildVenueHoverHtml, hoverCardColors } from './venueHoverCard';
import { DARK_MAP_STYLE } from './darkMapStyle';

// Derive the Google Maps types from the loader's library outputs so we don't
// depend on the (unresolvable) global `google.maps.*` namespace.
type MapsLibrary = Awaited<ReturnType<typeof importLibrary<'maps'>>>;
type MarkerLibrary = Awaited<ReturnType<typeof importLibrary<'marker'>>>;
type GMap = InstanceType<MapsLibrary['Map']>;
type GMarker = InstanceType<MarkerLibrary['Marker']>;
type GInfoWindow = InstanceType<MapsLibrary['InfoWindow']>;
/** The Map constructor's options object — used to type the dark-style cast. */
type MapOptions = NonNullable<ConstructorParameters<MapsLibrary['Map']>[1]>;
type MapListener = ReturnType<GMap['addListener']>;

// The v2 loader is a browser singleton: retries and view toggles reuse it.
// Calling setOptions again logs the supplied key even when it is unchanged.
let configuredLoaderKey: string | undefined;

export type MapCoordinates = { readonly lat: number; readonly lng: number };
export type MapCameraPosition = { readonly center: MapCoordinates; readonly zoom: number };

/** A new id expresses a deliberate camera movement; ordinary renders never do. */
export type MapCameraRequest =
  | {
      readonly id: string | number;
      readonly type: 'center';
      readonly center: MapCoordinates;
      readonly zoom?: number;
    }
  | {
      readonly id: string | number;
      readonly type: 'bounds';
      readonly bounds: MapBounds;
      readonly padding?: number;
      /** Applied once after fitting; later user zoom gestures stay unrestricted. */
      readonly maxZoom?: number;
    };

type MapRuntime = {
  readonly apiKey: string;
  readonly retryKey: string | number | undefined;
  readonly map: GMap;
  readonly Marker: MarkerLibrary['Marker'];
  readonly infoWindow: GInfoWindow;
};

export type MapViewProps = {
  readonly apiKey: string;
  readonly venues: readonly VenueListItem[];
  /** Mount-only fallback. Use cameraRequest for subsequent deliberate moves. */
  readonly initialBounds?: MapBounds;
  readonly cameraRequest?: MapCameraRequest;
  /** Drawn independently of venue clusters; does not move the camera. */
  readonly userLocation?: MapCoordinates | null;
  /** Keeps the selected venue visible as a distinct pin, even inside a cluster. */
  readonly activeVenueId?: string | null;
  readonly onVenueClick?: (venue: VenueListItem) => void;
  /** Reports settled camera state so callers can restore it after a view unmount. */
  readonly onCameraChange?: (camera: MapCameraPosition) => void;
  readonly onReady?: () => void;
  readonly onLoadError?: (error: Error) => void;
  /** Change this after a failed load to explicitly retry the map loader. */
  readonly retryKey?: string | number;
  readonly height?: number | string;
  readonly ariaLabel?: string;
  /**
   * Force the dark ("midnight") or light basemap. When omitted, the map
   * follows the active palette's mode (`useTokens().mode`) — dark palette →
   * dark map, light palette → Google light.
   */
  readonly dark?: boolean;
};

function applyCameraRequest(map: GMap, request: MapCameraRequest): void {
  if (request.type === 'bounds') {
    map.fitBounds(request.bounds, request.padding);
  } else {
    map.setCenter(request.center);
    if (request.zoom !== undefined) map.setZoom(request.zoom);
  }
}

/**
 * Pick the venue's primary sport from `sports[]`. Falls back to the first
 * listed sport, then to `football-5` if the venue has no sports at all
 * (defensive — F10 enforces ≥1 sport at creation).
 */
function pickPrimarySport(venue: VenueListItem): SportName {
  const primary = venue.sports.find((s) => s.isPrimary);
  if (primary) return primary.sportName;
  const first = venue.sports[0];
  if (first) return first.sportName;
  return 'football-5';
}

export function MapView({
  apiKey,
  venues,
  initialBounds,
  cameraRequest,
  userLocation,
  activeVenueId,
  onVenueClick,
  onCameraChange,
  onReady,
  onLoadError,
  retryKey,
  height = 480,
  ariaLabel,
  dark,
}: MapViewProps): React.ReactElement {
  const t = useTokens();
  const { palette } = usePalette();
  // An explicit `dark` prop wins; otherwise follow the active palette's mode.
  const isDark = dark ?? t.mode === 'dark';
  const ref = useRef<HTMLDivElement>(null);
  const [runtime, setRuntime] = useState<MapRuntime | null>(null);
  const [bounds, setBounds] = useState<MapBounds | null>(initialBounds ?? null);
  const [zoom, setZoom] = useState(8);
  const callbacks = useRef({ onReady, onLoadError, onVenueClick, onCameraChange, cameraRequest });
  const appliedCamera = useRef<{ map: GMap; id: string | number } | null>(null);
  const pendingFitZoom = useRef<{ map: GMap; maxZoom: number } | null>(null);
  // Loader/marker callbacks use current props without resetting the map or pins.
  useEffect(() => {
    callbacks.current = { onReady, onLoadError, onVenueClick, onCameraChange, cameraRequest };
  }, [onReady, onLoadError, onVenueClick, onCameraChange, cameraRequest]);

  const currentRuntime =
    runtime?.apiKey === apiKey && runtime.retryKey === retryKey ? runtime : null;
  const map = currentRuntime?.map;
  const markerCtor = currentRuntime?.Marker;
  const infoWindow = currentRuntime?.infoWindow;

  // Only an explicit retry or API-key change replaces the map instance.
  useEffect(() => {
    if (!ref.current) return;
    const container = ref.current;
    let cancelled = false;
    let idleListener: MapListener | undefined;
    let loadedInfoWindow: GInfoWindow | undefined;

    const load = async () => {
      try {
        if (configuredLoaderKey !== apiKey) {
          setOptions({ key: apiKey });
          configuredLoaderKey = apiKey;
        }
        const [mapsLib, markerLib] = await Promise.all([
          importLibrary('maps'),
          importLibrary('marker'),
        ]);
        if (cancelled) return;
        const m = new mapsLib.Map(container, {
          center: { lat: 33.9, lng: 35.5 },
          zoom: 8,
          disableDefaultUI: false,
          gestureHandling: 'greedy',
        });
        const request = callbacks.current.cameraRequest;
        if (request) {
          pendingFitZoom.current =
            request.type === 'bounds' ? { map: m, maxZoom: request.maxZoom ?? 16 } : null;
          applyCameraRequest(m, request);
          appliedCamera.current = { map: m, id: request.id };
        } else if (initialBounds) {
          m.fitBounds(initialBounds);
        }
        idleListener = m.addListener('idle', () => {
          if (cancelled) return;
          const fit = pendingFitZoom.current;
          if (fit && fit.map === m) {
            pendingFitZoom.current = null;
            if ((m.getZoom() ?? 8) > fit.maxZoom) m.setZoom(fit.maxZoom);
          }
          const b = m.getBounds();
          if (b) {
            const ne = b.getNorthEast();
            const sw = b.getSouthWest();
            setBounds({
              west: sw.lng(),
              south: sw.lat(),
              east: ne.lng(),
              north: ne.lat(),
            });
          }
          const settledZoom = m.getZoom();
          if (settledZoom !== undefined && Number.isFinite(settledZoom)) {
            setZoom(settledZoom);
            const center = m.getCenter();
            if (center) {
              const lat = center.lat();
              const lng = center.lng();
              if (Number.isFinite(lat) && Number.isFinite(lng)) {
                callbacks.current.onCameraChange?.({ center: { lat, lng }, zoom: settledZoom });
              }
            }
          }
        });
        loadedInfoWindow = new mapsLib.InfoWindow({ disableAutoPan: true });
        setBounds(initialBounds ?? null);
        setZoom(m.getZoom() ?? 8);
        setRuntime({
          apiKey,
          retryKey,
          map: m,
          Marker: markerLib.Marker,
          infoWindow: loadedInfoWindow,
        });
        callbacks.current.onReady?.();
      } catch (error) {
        if (!cancelled) {
          callbacks.current.onLoadError?.(
            error instanceof Error ? error : new Error(String(error)),
          );
        }
      }
    };
    void load();

    return () => {
      cancelled = true;
      idleListener?.remove();
      loadedInfoWindow?.close();
      container.replaceChildren();
    };
    // initialBounds remains mount-only for existing consumers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, retryKey]);

  useEffect(() => {
    if (!map || !cameraRequest) return;
    const applied = appliedCamera.current;
    if (applied && applied.map === map && applied.id === cameraRequest.id) return;
    pendingFitZoom.current =
      cameraRequest.type === 'bounds' ? { map, maxZoom: cameraRequest.maxZoom ?? 16 } : null;
    applyCameraRequest(map, cameraRequest);
    appliedCamera.current = { map, id: cameraRequest.id };
  }, [map, cameraRequest]);

  // Apply (or clear) the dark basemap style once the map exists and whenever
  // the resolved mode flips (palette toggle or explicit prop) — no remount.
  useEffect(() => {
    if (!map) return;
    map.setOptions({ styles: (isDark ? DARK_MAP_STYLE : null) as MapOptions['styles'] });
  }, [map, isDark]);

  const clusteredVenues = useMemo(
    () => (activeVenueId ? venues.filter((venue) => venue.id !== activeVenueId) : venues),
    [venues, activeVenueId],
  );
  const activeVenue = venues.find((venue) => venue.id === activeVenueId);
  const clusters = useMapClusters(clusteredVenues, bounds, zoom);

  // Redraw markers whenever the cluster set or the active theme changes
  useEffect(() => {
    if (!map || !markerCtor) return;
    const markers: GMarker[] = [];
    const listeners: MapListener[] = [];
    let cancelled = false;

    const features = clusters.map((cluster) => ({ ...cluster, selected: false }));
    if (activeVenue) {
      const lat = Number.parseFloat(activeVenue.lat);
      const lng = Number.parseFloat(activeVenue.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: { venue: activeVenue },
          selected: true,
        });
      }
    }

    void Promise.all(
      features.map(async (c) => {
        const [lng, lat] = c.geometry.coordinates;
        const props = c.properties as
          | { readonly cluster: true; readonly point_count: number }
          | { readonly venue: VenueListItem };
        const isCluster = 'cluster' in props && props.cluster === true;

        let icon: string;
        if (isCluster) {
          const clusterProps = props as { readonly point_count: number };
          icon = composeClusterBubbleSvg(
            clusterProps.point_count,
            t.color.accent,
            t.color.accentInk,
          );
        } else {
          const { venue } = props as { readonly venue: VenueListItem };
          icon = await composeVenuePinSvg({
            sportKey: pickPrimarySport(venue),
            tier: venue.tier,
            accentHex: t.color.accent,
            inkHex: t.color.ink,
            selected: c.selected,
          });
        }
        // Asset loading may finish after a pan, palette change, or unmount.
        if (cancelled) return;

        const marker = new markerCtor({
          position: { lat, lng },
          map,
          icon,
          ...(c.selected ? { zIndex: 1000 } : {}),
        });

        listeners.push(
          marker.addListener('click', () => {
            if (isCluster) {
              map.setCenter({ lat, lng });
              map.setZoom((map.getZoom() ?? zoom) + 2);
            } else {
              const { venue } = props as { readonly venue: VenueListItem };
              callbacks.current.onVenueClick?.(venue);
            }
          }),
        );

        // Hover an individual pin → show a small card (name + status + sports).
        if (!isCluster) {
          const { venue } = props as { readonly venue: VenueListItem };
          listeners.push(
            marker.addListener('mouseover', () => {
              infoWindow?.setContent(buildVenueHoverHtml(venue, hoverCardColors(palette)));
              infoWindow?.open({ anchor: marker, map });
            }),
          );
          listeners.push(marker.addListener('mouseout', () => infoWindow?.close()));
        }

        markers.push(marker);
      }),
    ).catch(() => {
      // Marker artwork has its own fallback; don't turn a pin failure into a
      // basemap load failure or leave an unhandled async rejection.
    });

    // Cleanup on unmount or before next redraw
    return () => {
      cancelled = true;
      for (const listener of listeners) listener.remove();
      for (const marker of markers) marker.setMap(null);
      infoWindow?.close();
    };
  }, [
    clusters,
    map,
    markerCtor,
    infoWindow,
    activeVenue,
    palette,
    t.color.accent,
    t.color.accentInk,
    t.color.ink,
    zoom,
  ]);

  const userLat = userLocation?.lat;
  const userLng = userLocation?.lng;
  useEffect(() => {
    if (!map || !markerCtor || userLat === undefined || userLng === undefined) return;
    if (!Number.isFinite(userLat) || !Number.isFinite(userLng)) return;
    const marker = new markerCtor({
      map,
      position: { lat: userLat, lng: userLng },
      // A symbol is anchored at its origin, keeping the dot exactly on the
      // location instead of using a pin image's bottom-center anchor.
      icon: {
        path: 'M 0 -8 A 8 8 0 1 1 0 8 A 8 8 0 1 1 0 -8',
        fillColor: t.color.accent,
        fillOpacity: 1,
        strokeColor: t.color.surface,
        strokeWeight: 3,
        scale: 1,
      },
      clickable: false,
      zIndex: 2000,
    });
    return () => marker.setMap(null);
  }, [map, markerCtor, userLat, userLng, t.color.accent, t.color.surface]);

  return (
    <div ref={ref} role="application" aria-label={ariaLabel} style={{ width: '100%', height }} />
  );
}
